import React, { useRef, useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useStaticQuery, graphql } from 'gatsby';
import { animated, useSpring, config } from 'react-spring';
// eslint-disable-next-line import/no-unresolved
import { useLocation } from '@reach/router';
import { ResponsiveContext } from 'grommet';
import Link from '../Link';
// eslint-disable-next-line import/no-cycle
import { DemoButton, LogInButton } from './Header';
import AssetPageIcon from './icons/asset-page-icon.inline.svg';

const StyledCarat = styled(animated.div)`
  height: 44px;
  width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  svg {
    width: 25px;
    height: 13px;
  }

  @media ${({ theme }) => theme.custom.layouts.above_large} {
    display: none;
  }
`;

const Carat = ({ onClick, isOpen }) => {
  const caratAnimation = useSpring({
    from: {
      transform: `rotate(180deg)`,
    },
    to: {
      transform: `rotate(${isOpen ? '0deg' : '180deg'})`,
    },
  });

  return (
    <StyledCarat style={caratAnimation} onClick={onClick}>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 29 17">
        <path d="M25.6 16.9L14.5 5.8 3.4 16.9.6 14.1 14.5.2l13.9 13.9z" />
      </svg>
    </StyledCarat>
  );
};

Carat.propTypes = {
  onClick: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
};

const PageIcon = styled.div`
  flex: 0 0 auto;
  height: 25px;
  width: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 25px;
    height: 25px;
  }
`;

const StyledArrow = styled.div`
  /* background: yellow; */
  flex: 0 0 auto;
  height: 11px;
  width: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 12px;
    height: 11px;
  }
`;

const Arrow = () => (
  <StyledArrow>
    <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 11">
      <path
        d="M6.513 0L5.088 1.414l3.088 3.088H0v1.996h8.177L5.089 9.586 6.512 11 12 5.5 6.513 0z"
        fill="#000"
      />
    </svg>
  </StyledArrow>
);

const SharedNavItemActiveStyle = css`
  background-color: ${({ theme }) => theme.global.colors.brand};
  a,
  span {
    color: white;
  }

  svg path {
    fill: white;
  }
`;

const SharedNavItemStyles = css`
  padding: 0 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;

  @media ${({ theme }) => theme.custom.layouts.below_large} {
    border-bottom: solid 1px ${({ theme }) => theme.global.colors['light-3']};
  }

  @media ${({ theme }) => theme.custom.layouts.above_large} {
    padding: 0 20px;
  }

  svg path {
    transition: all 0.3s ease;
  }

  > a,
  > span {
    /* link */
    line-height: ${({ theme }) => theme.custom.helpers.pxToRem(50)};
    flex: 1 1 100%;

    font-family: ${({ theme }) => theme.custom.type.sans};
    font-size: ${({ theme }) => theme.custom.helpers.pxToRem(20)};
    text-decoration: none;

    transition: color 0.2s ease;
    color: black;
    position: relative;

    &:hover {
      text-decoration: none;
    }

    @media ${({ theme }) => theme.custom.layouts.above_large} {
      font-size: ${({ theme }) => theme.custom.helpers.pxToRem(16)};

      &:after {
        content: '';
        position: absolute;
        bottom: 10px;
        display: block;
        height: 3px;
        width: 0%;
        opacity: 0;
        transition: width 0.1s ease, opacity 0.3s ease;
        background-color: ${({ theme }) => theme.global.colors.brand};
      }
    },
  },
  > span {
    &:hover {
      text-decoration: none;
      cursor: default;
    }
  }
`;

// === Secondary Nav Item ===

const SecondaryNavItemTitle = styled.div`
  ${SharedNavItemStyles};

  @media ${({ theme }) => theme.custom.layouts.above_large} {
    padding: 0 20px;
  }

  > a {
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }
  > a span {
    flex: 1 1 100%;
  }

  ${({ isActive }) => isActive && SharedNavItemActiveStyle};

  @media (hover: hover) {
    &:hover {
      ${SharedNavItemActiveStyle};
    }
  }
`;

const StyledSecondaryNavDrawer = styled(animated.div)`
  height: ${({ $height }) => $height};
  background-color: ${({ theme }) => theme.global.colors['light-2']};
  @media ${({ theme }) => theme.custom.layouts.above_large} {
    /* fake shadow! */
    background-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.05) 0px,
      white 5px
    );
  }

  overflow: hidden;
  @media ${({ theme }) => theme.custom.layouts.above_large} {
    position: absolute;
    top: 65px;
    left: 10px;
    width: 320px;
    box-shadow: 0 10px 10px 0 rgba(0, 0, 0, 0.2);
  }
`;

const SecondaryNavItemIcon = ({ path }) => {
  switch (path) {
    case '/features/assets':
      return <AssetPageIcon />;
    default:
      return null;
  }
};
SecondaryNavItemIcon.propTypes = {
  path: PropTypes.string.isRequired,
};

const SecondaryNavDrawer = ({ items, isOpen }) => {
  const linkListRef = useRef();
  const location = useLocation();

  return (
    <StyledSecondaryNavDrawer
      $height={isOpen ? `${linkListRef.current.offsetHeight}px` : `0px`}
    >
      <div ref={linkListRef}>
        {items.map(({ title, href }) => {
          const isActive = location.pathname.replace(/\/$/, '') === href;
          return (
            <SecondaryNavItemTitle isActive={isActive} key={title}>
              <Link href={href}>
                <span>{title}</span>
                {/* <Arrow /> */}
              </Link>
            </SecondaryNavItemTitle>
          );
        })}
      </div>
    </StyledSecondaryNavDrawer>
  );
};

SecondaryNavDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      href: PropTypes.string,
    }),
  ).isRequired,
};

// === Primary Nav Item ===
const PrimaryNavItemTitleActiveStyle = css`
  /* On Mobile, all active nav item titles look the same. */
  @media ${({ theme }) => theme.custom.layouts.below_large} {
    ${({ isActive }) => {
      if (!isActive) return null;
      return SharedNavItemActiveStyle;
    }}
  }
  @media ${({ theme }) => theme.custom.layouts.above_large} {
    a,
    span {
      &:after {
        opacity: 1;
        transition: width 0.3s ease, opacity 0.3s ease;
        width: 100%;
      }
    }
  }
`;

const NavItemTitle = styled.div`
  ${SharedNavItemStyles}

  > a + svg {
    margin-left: ${({ hasItems }) => (hasItems ? '20px' : '0px')};
  }
  > ${StyledCarat} {
    flex: 0 0 auto;
  }

  ${({ isActive, isOpen }) =>
    (isActive || isOpen) && PrimaryNavItemTitleActiveStyle};

  /* HOVERS */
  @media (hover: hover) {
    &:hover {
      @media ${({ theme }) => theme.custom.layouts.below_large} {
        ${SharedNavItemActiveStyle};
      }
      @media ${({ theme }) => theme.custom.layouts.above_large} {
        ${PrimaryNavItemTitleActiveStyle};
      }
    }
  }
`;

const StyledPrimaryNavItem = styled.div`
  position: relative;
  overflow: visible;
  /* This is to patch the issue of menu timeout delay creating overlapping menus between top level navigation,
  but a lower timeout would make it harder to select the menu below the navigation if the user moved too slowly.
  This spacer is to keep the element in a "hovered" state even if the user moves slowly down to the menu underneath. */
  @media ${({ theme }) => theme.custom.layouts.above_large} {
    &:after {
      position: absolute;
      content: '';
      bottom: -15px;
      left: 0;
      right: 0;
      height: 15px;
      display: block;
    }
  }
`;

const PrimaryNavItem = ({
  title,
  href,
  items,
  isActive,
  drawerIsOpen,
  shouldNavigate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const layout = useContext(ResponsiveContext);
  const useMobileLayout = layout === 'medium' || layout === 'small';
  const hoverTimeout = useRef();
  const location = useLocation();

  useEffect(() => {
    // check to see if a child of one of these items is the current page
    // and open if so
    if (
      items &&
      items.filter((item) => item.href === location.pathname).length !== 0
    ) {
      setIsOpen(true);
    }
  }, [useMobileLayout]);

  useEffect(() => {
    // close this is the parent drawer closes
    if ((isOpen && !drawerIsOpen) || !useMobileLayout) setIsOpen(false);
  }, [drawerIsOpen, useMobileLayout]);

  const handleMouseEnter = () => {
    if (useMobileLayout) return null;
    clearTimeout(hoverTimeout.current);
    setIsOpen(true);
    return null;
  };
  const handleMouseLeave = () => {
    if (useMobileLayout) return null;
    clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => {
      setIsOpen(false);
    }, 10);
    return null;
  };

  return (
    <StyledPrimaryNavItem
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <NavItemTitle
        isActive={isActive}
        isOpen={isOpen}
        hasItems={items?.length}
      >
        {shouldNavigate === true ? (
          <Link href={href}>{title}</Link>
        ) : (
          <span>{title}</span>
        )}

        {items && useMobileLayout && (
          <Carat isOpen={isOpen} onClick={() => setIsOpen(!isOpen)} />
        )}
      </NavItemTitle>

      {items && <SecondaryNavDrawer isOpen={isOpen} items={items} />}
    </StyledPrimaryNavItem>
  );
};

PrimaryNavItem.defaultProps = {
  items: [],
  href: null,
  shouldNavigate: true,
};
PrimaryNavItem.propTypes = {
  shouldNavigate: PropTypes.bool,
  isActive: PropTypes.bool.isRequired,
  drawerIsOpen: PropTypes.bool.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      href: PropTypes.string,
    }),
  ),
  title: PropTypes.string.isRequired,
  href: PropTypes.string,
};

// === Navigation ===

const StyledNavigation = styled(animated.nav)`
  @media ${({ theme }) => theme.custom.layouts.below_large} {
    width: 100%;
    position: absolute;
    top: var(--headerHeight);
    z-index: 9001;
    background: white;
    left: 0;
    overflow: ${({ $isOpen }) => ($isOpen ? `auto` : 'hidden')};
    transition: all 0.5s ease;
    box-shadow: inset 0 0 10px 0 rgb(0 0 0 / 20%);
    height: ${({ $isOpen }) =>
      $isOpen ? `calc(100vh - var(--headerHeight))` : '0px'};
    > div:first-of-type {
      /* nav items */
      display: flex;
      flex-direction: column;
    }
    > div:last-of-type {
      /* buttons items */
      padding: 40px 30px;
      > *:nth-of-type(1) {
        margin-bottom: 20px;
      }
    }
  }

  @media ${({ theme }) => theme.custom.layouts.above_large} {
    flex: 1 1 100%;
    /* background: pink; */
    display: flex;
    align-items: center;
    > div {
      /* nav items */
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
    }
    > div:first-of-type {
      flex: 1 1 100%;
      padding-right: 20px;
    }
    > div:last-of-type {
      /* buttons */
      flex: 0 1 auto;
    }
  }
`;

const Navigation = ({ isOpen }) => {
  const {
    site: {
      siteMetadata: { pages: pageItems },
    },
  } = useStaticQuery(graphql`
    {
      site {
        siteMetadata {
          pages {
            title
            shouldNavigate
            href
            items {
              href
              title
            }
          }
        }
      }
    }
  `);

  const location = useLocation();
  return (
    <StyledNavigation $isOpen={isOpen}>
      <div>
        {pageItems &&
          pageItems.map((page) => {
            const isActive = location.pathname.includes(page.href);
            return (
              <PrimaryNavItem
                isActive={isActive}
                drawerIsOpen={isOpen}
                key={page.title}
                {...page}
              />
            );
          })}
      </div>
      <div>
        <DemoButton />
      </div>
    </StyledNavigation>
  );
};

Navigation.propTypes = {
  isOpen: PropTypes.bool.isRequired,
};

export default Navigation;
